/**
 * @packageDocumentation
 * @module f1/string
 */

import uncapitalize from './uncapitalize';
import capitalize from './capitalize';

/** @ignore break-on-kebab-to-snake */
const brkKS = /-/g;
/** @ignore break-on-snake-to-kebab */
const bkrSK = /_/g;
/** @ignore break-on-word-boundary */
const brkWB = /[A-Z]/g;

type t_case = 'camel' | 'kebab' | 'pascal' | 'snake';

/** @ignore */
const dashes = (dash: '-' | '_', s: string) =>
  s.replace(brkWB, (s, index) => (index === 0 ? '' : dash) + s).toLowerCase();

/** @ignore */
const conv = {
  camel: {
    kebab(s: string) {
      return dashes('-', s);
    },
    pascal(s: string) {
      return capitalize(s);
    },
    snake(s: string) {
      return dashes('_', s);
    },
    camel(s: string) {
      return s;
    }
  },
  kebab: {
    pascal(s: string) {
      return s.split('-').reduce((s, w) => s + capitalize(w), '');
    },
    snake(s: string) {
      return s.replace(brkKS, '_');
    },
    camel(s: string) {
      return uncapitalize(this.pascal(s));
    },
    kebab(s: string) {
      return s;
    }
  },
  pascal: {
    snake(s: string) {
      return dashes('_', s);
    },
    camel(s: string) {
      return uncapitalize(s);
    },
    kebab(s: string) {
      return dashes('-', s);
    },
    pascal(s: string) {
      return s;
    }
  },
  snake: {
    camel(s: string) {
      return s
        .split('_')
        .map((w, index) => (index === 0 ? w : capitalize(w)))
        .join('');
    },
    kebab(s: string) {
      return s.replace(bkrSK, '-');
    },
    pascal(s: string) {
      return capitalize(this.camel(s));
    },
    snake(s: string) {
      return s;
    }
  }
};

export interface Form {
  /**
   * Convert strings of common and useful forms (for programming) to other forms
   *
   * @public
   * @function f1.form
   * @param form the form of the string converting from
   * @param to the form of the string converting to
   * @param s a string matching the `from` form
   * @example
   *
   * ```ts
   * f1.form('camel', 'kebab', 'hiButton') === 'hi-button'
   * ```
   */
  (from: t_case, to: t_case, s: string): string;
  (from: t_case, to: t_case): (s: string) => string;
}

/**
 * @see Form
 */
export default (from: t_case, to: t_case, s: string) => {
  return conv[from][to](s);
};
